<template>
  <div>
    <!-- Header -->
    <section class="page-header">
      <div class="premium-container">
        <div class="fade-in">
          <span class="premium-label">Contact</span>
          <h1 class="premium-heading-lg">
            Let's talk about<br>your project.
          </h1>
          <p class="premium-subheading">
            Tell us what you're working on. We'll get back to you
            within 24 hours with a free consultation.
          </p>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="premium-section-alt">
      <div class="premium-container">
        <div class="contact-layout">
          <!-- Form -->
          <div class="contact-form-wrap fade-in">
            <v-alert
              v-if="successMessage"
              type="success"
              variant="tonal"
              class="mb-6"
              closable
              rounded="lg"
              @click:close="successMessage = ''"
            >
              {{ successMessage }}
            </v-alert>

            <v-alert
              v-if="errorMessage"
              type="error"
              variant="tonal"
              class="mb-6"
              closable
              rounded="lg"
              @click:close="errorMessage = ''"
            >
              {{ errorMessage }}
            </v-alert>

            <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
              <div class="form-grid">
                <v-text-field
                  v-model="formData.name"
                  label="Full name"
                  :rules="[rules.required, rules.minLength]"
                  class="form-field"
                  autocomplete="name"
                  hide-details="auto"
                ></v-text-field>

                <v-text-field
                  v-model="formData.email"
                  label="Email address"
                  :rules="[rules.required, rules.email]"
                  class="form-field"
                  type="email"
                  autocomplete="email"
                  hide-details="auto"
                ></v-text-field>

                <v-text-field
                  v-model="formData.phone"
                  label="Phone number (optional)"
                  class="form-field"
                  autocomplete="tel"
                  hide-details="auto"
                ></v-text-field>

                <v-text-field
                  v-model="formData.company"
                  label="Company name (optional)"
                  class="form-field"
                  autocomplete="organization"
                  hide-details="auto"
                ></v-text-field>
              </div>

              <v-select
                v-model="formData.service_type"
                :items="serviceTypes"
                label="What are you looking for?"
                :rules="[rules.required]"
                class="form-field mt-6"
                hide-details="auto"
              ></v-select>

              <v-textarea
                v-model="formData.message"
                label="Tell us about your project"
                :rules="[rules.required, rules.messageLength]"
                rows="5"
                class="form-field mt-6"
                hide-details="auto"
              ></v-textarea>

              <v-btn
                type="submit"
                size="x-large"
                block
                :loading="loading"
                :disabled="!valid"
                class="mt-8 submit-btn"
                aria-label="Send your message to Digitly"
              >
                Send message
              </v-btn>
            </v-form>
          </div>

          <!-- Sidebar -->
          <div class="contact-sidebar fade-in fade-in-delay-2">
            <div class="info-card">
              <h3 class="info-title">Contact details</h3>

              <div class="info-item">
                <span class="info-label">Email</span>
                <a href="mailto:georgepavlej@gmail.com" class="info-value info-link">georgepavlej@gmail.com</a>
              </div>

              <div class="info-item">
                <span class="info-label">Phone</span>
                <a href="tel:+447897513276" class="info-value info-link">+44 78 9751 3276</a>
              </div>

              <div class="info-item">
                <span class="info-label">Hours</span>
                <span class="info-value">Mon – Sat, 9am – 6pm GMT</span>
              </div>

              <div class="info-item">
                <span class="info-label">Response</span>
                <span class="info-value">Within 24 hours</span>
              </div>
            </div>

            <div class="info-card">
              <h3 class="info-title">Starting from</h3>
              <p class="price-display">&pound;499</p>
              <p class="price-note premium-body">
                Every project is quoted individually based on your specific
                requirements. Get in touch for a free, no-obligation quote.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="premium-section">
      <div class="premium-container">
        <div class="faq-layout">
          <div class="faq-header fade-in">
            <span class="premium-label">FAQ</span>
            <h2 class="premium-heading">
              Common questions.
            </h2>
          </div>
          <div class="faq-list fade-in fade-in-delay-1">
            <div
              v-for="(faq, index) in faqs"
              :key="index"
              class="faq-item"
              @click="toggleFaq(index)"
            >
              <div class="faq-question">
                <h3 class="faq-q-text">{{ faq.question }}</h3>
                <span class="faq-toggle" :class="{ 'faq-open': openFaq === index }">+</span>
              </div>
              <transition name="faq-expand">
                <div v-if="openFaq === index" class="faq-answer">
                  <p class="premium-body">{{ faq.answer }}</p>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from '@/axios.config'
import { useSeo, useFaqSchema, useBreadcrumbSchema } from '@/composables/useSeo'

export default {
  name: 'Contact',
  setup() {
    const faqs = [
      {
        question: 'How long does it take to build a website?',
        answer: 'Most standard websites are delivered in 2–3 weeks. More complex projects with custom automation or AI features may take a little longer. We pride ourselves on fast turnaround without cutting corners.'
      },
      {
        question: 'What is the cost of your services?',
        answer: 'Our websites start from £499, with pricing based on the scope and features you need. We provide clear, fixed-price quotes upfront — no surprises. Get in touch for a free, no-obligation quote.'
      },
      {
        question: 'Do you provide ongoing support?',
        answer: 'Yes. We offer maintenance and support packages starting from £99/month. This includes regular updates, security monitoring, backups, and technical support to keep your website running smoothly.'
      },
      {
        question: 'Can you help with an existing website?',
        answer: 'Absolutely. We can help improve, redesign, or add new features to your existing website. We work with various platforms and technologies to enhance your current digital presence.'
      },
      {
        question: 'Do you offer custom solutions?',
        answer: 'Yes, we specialise in custom solutions tailored to your unique business needs. Whether you need a booking system, CRM, or specialised automation, we can build it for you.'
      }
    ]

    useSeo({
      title: 'Contact Us - Get a Free Quote',
      description: 'Contact Digitly for a free web development quote. We respond within 24 hours. Call +44 78 9751 3276 or fill out our contact form. Websites from £499, delivered in 2-3 weeks.',
      keywords: 'contact Digitly, free web development quote, website enquiry, get in touch, UK web design contact',
      path: '/contact'
    })

    useFaqSchema(faqs)

    useBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Contact', path: '/contact' }
    ])

    return { faqs }
  },
  data() {
    return {
      valid: false,
      loading: false,
      successMessage: '',
      errorMessage: '',
      openFaq: null,
      formData: {
        name: '',
        email: '',
        phone: '',
        company: '',
        service_type: '',
        message: ''
      },
      serviceTypes: [
        'Website Development',
        'AI & Automation',
        'E-Commerce Solutions',
        'Business Automation',
        'Booking System',
        'SEO & Marketing',
        'Maintenance & Support',
        'Custom Solution',
        'General Inquiry'
      ],
      rules: {
        required: value => !!value || 'This field is required',
        minLength: value => (value && value.length >= 2) || 'Minimum 2 characters required',
        email: value => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return pattern.test(value) || 'Invalid email address'
        },
        messageLength: value => (value && value.length >= 10) || 'Please provide more details (minimum 10 characters)'
      }
    }
  },
  methods: {
    toggleFaq(index) {
      this.openFaq = this.openFaq === index ? null : index
    },
    async submitForm() {
      if (!this.valid) return

      this.loading = true
      this.successMessage = ''
      this.errorMessage = ''

      try {
        const response = await axios.post('/api/contact/', this.formData)

        if (response.data.success) {
          this.successMessage = response.data.message
          this.resetForm()
        }
      } catch (error) {
        console.error('Error submitting form:', error)
        if (error.response && error.response.data && error.response.data.detail) {
          this.errorMessage = error.response.data.detail
        } else {
          this.errorMessage = 'Failed to send message. Please try again or contact us directly.'
        }
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.formData = {
        name: '',
        email: '',
        phone: '',
        company: '',
        service_type: '',
        message: ''
      }
      this.$refs.form.resetValidation()
    }
  }
}
</script>

<style scoped>
.page-header {
  padding: 140px 0 80px;
  background: var(--color-bg);
}

.contact-layout {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 80px;
  align-items: start;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.submit-btn {
  background: var(--color-accent) !important;
  color: #FFFFFF !important;
  font-weight: 600;
  font-family: var(--font-family);
  letter-spacing: -0.01em;
  text-transform: none;
  font-size: 1rem;
}

.contact-sidebar {
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: sticky;
  top: 96px;
}

.info-card {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 32px;
}

.info-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-text);
  margin-bottom: 24px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 0;
  border-bottom: 1px solid var(--color-border);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 0.8125rem;
  color: var(--color-text-tertiary);
}

.info-value {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text);
}

.info-link {
  text-decoration: none;
  color: var(--color-text);
  transition: color 0.15s ease;
}

.info-link:hover {
  color: var(--color-link);
}

.price-display {
  font-size: 3rem;
  font-weight: 800;
  color: var(--color-text);
  letter-spacing: -0.04em;
  line-height: 1;
  margin-bottom: 12px;
}

.price-note {
  font-size: 0.875rem;
}

.faq-layout {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 80px;
  align-items: start;
}

.faq-list {
  display: flex;
  flex-direction: column;
}

.faq-item {
  padding: 24px 0;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
}

.faq-item:first-child {
  border-top: 1px solid var(--color-border);
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.faq-q-text {
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.faq-toggle {
  font-size: 1.25rem;
  font-weight: 300;
  color: var(--color-text-secondary);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.faq-open {
  transform: rotate(45deg);
}

.faq-answer {
  margin-top: 12px;
  padding-right: 40px;
}

.faq-expand-enter-active,
.faq-expand-leave-active {
  transition: all 0.2s ease;
}

.faq-expand-enter-from,
.faq-expand-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 960px) {
  .page-header {
    padding: 56px 0 56px;
  }

  .contact-layout {
    grid-template-columns: 1fr;
    gap: 48px;
  }

  .contact-sidebar {
    position: static;
  }

  .faq-layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 36px 0 48px;
  }
}
</style>
